import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { TargetBadgeInput } from "_graphql/__generated__/globalTypes";
import { CreateTargetBadgesVariables } from "_graphql/mutation/__generated__/CreateTargetBadges";
import { OfficeButton } from "components/buttons";
import { RegularSelectInput, TextArea, TextInput } from "components/core";
import { FieldArray, FormikProps, FormikProvider } from "formik";
import { FC, useState } from "react";
import toast from "react-hot-toast";

const GIFT_TYPES = [
  "CASH PRIZE",
  "GIFT ITEM",
  "VOUCHER",
  "TRAVEL",
  "ELECTRONICS",
];

export interface CreateTargetBadgeFormProps {
  form: FormikProps<CreateTargetBadgesVariables>;
}

const isErrorObject = (error: any): error is Record<string, any> => {
  return error !== null && typeof error === "object";
};

const getErrorMessage = (
  errors: any | undefined,
  path: string
): string | undefined => {
  if (!errors) return undefined;

  const pathParts = path.split(".");
  let current: any = errors;

  for (const part of pathParts) {
    if (!isErrorObject(current)) return undefined;
    current = current[part];
    if (current === undefined) return undefined;
  }

  return typeof current === "string" ? current : undefined;
};

const CreateTargetBadgeForm: FC<CreateTargetBadgeFormProps> = ({ form }) => {
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);

  const renderFieldError = (fieldPath: string) => {
    const pathParts = fieldPath.split(".");
    const touchedValue = pathParts.reduce(
      (acc: any, part) => acc && acc[part],
      form.touched
    );
    const errorMsg = getErrorMessage(form.errors, fieldPath);

    if (touchedValue && errorMsg) {
      return <p className="mt-1 text-sm text-red-600">{errorMsg}</p>;
    }
    return null;
  };

  const initialBadge: TargetBadgeInput = {
    name: "",
    min_points: 0,
    max_points: 500,
    description: "",
    cash_equivalent: 0,
    minimum_cash_equivalent: 0,
    gift_basket: [],
  };

  const addGift = (badgeIndex: number) => {
    const badges = [...form.values.badges];
    const badge = badges?.[badgeIndex];
    const giftBasket = badge?.gift_basket || [];
    if (badge && giftBasket.length < 5) {
      giftBasket.push({
        gift_name: "",
        gift_type: "",
        gift_image: "",
        gift_description: "",
      });
      form.setFieldValue("badges", badges);
    } else {
      toast.error("Maximum 5 gifts allowed per badge");
    }
  };

  const removeGift = (badgeIndex: number, giftIndex: number) => {
    const badges = [...form.values.badges];
    badges?.[badgeIndex]?.gift_basket?.splice(giftIndex, 1);
    form.setFieldValue("badges", badges);
  };

  const addBadge = () => {
    form.setFieldValue("badges", [...form.values.badges, { ...initialBadge }]);
    setCurrentBadgeIndex(form.values.badges.length);
  };

  const removeBadge = (index: number) => {
    const badges = [...form.values.badges];
    badges.splice(index, 1);
    form.setFieldValue("badges", badges);
    if (currentBadgeIndex >= badges.length) {
      setCurrentBadgeIndex(Math.max(0, badges.length - 1));
    }
  };

  return (
    <FormikProvider value={form}>
      <div className="space-y-4 py-4 no-scrollbar">
        {/* Badge tabs */}
        <div className="flex space-x-2 overflow-x-auto pb-2 border-b">
          {form.values.badges.map((badge: TargetBadgeInput, index: number) => (
            <div
              key={index}
              className={`px-3 py-2 text-sm font-medium rounded-t-md cursor-pointer whitespace-nowrap flex items-center space-x-2 ${
                currentBadgeIndex === index
                  ? "bg-primary-100 text-primary-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setCurrentBadgeIndex(index)}
            >
              <span>{badge.name || `Badge ${index + 1}`}</span>
              {form.values.badges.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeBadge(index);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <div
            className="px-3 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-t-md cursor-pointer"
            onClick={addBadge}
          >
            <PlusIcon className="h-5 w-5" />
          </div>
        </div>

        {/* Current badge form */}
        {form.values.badges.map(
          (badge: TargetBadgeInput, badgeIndex: number) => {
            // const badge = form?.values?.badges?.[badgeIndex];

            return (
              <div
                key={badgeIndex}
                className={currentBadgeIndex === badgeIndex ? "" : "hidden"}
              >
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor={`badges[${badgeIndex}].name`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      Badge Name
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id={`badges[${badgeIndex}].name`}
                        name={`badges[${badgeIndex}].name`}
                        value={form.values.badges[badgeIndex].name}
                        onChange={form.handleChange}
                        onBlur={form.handleBlur}
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50"
                        placeholder="Bronze, Silver, Gold, etc."
                        disabled
                      />
                      {renderFieldError(`badges.${badgeIndex}.name`)}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor={`badges[${badgeIndex}].min_points`}
                        className="block text-sm font-medium text-gray-700"
                      >
                        Min Points
                      </label>
                      <div className="mt-1">
                        <input
                          type="number"
                          id={`badges[${badgeIndex}].min_points`}
                          name={`badges[${badgeIndex}].min_points`}
                          value={form.values.badges[badgeIndex].min_points}
                          onChange={form.handleChange}
                          onBlur={form.handleBlur}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50"
                          min="0"
                          disabled
                        />
                        {renderFieldError(`badges.${badgeIndex}.min_points`)}
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor={`badges[${badgeIndex}].max_points`}
                        className="block text-sm font-medium text-gray-700"
                      >
                        Max Points
                      </label>
                      <div className="mt-1">
                        <input
                          type="number"
                          id={`badges[${badgeIndex}].max_points`}
                          name={`badges[${badgeIndex}].max_points`}
                          value={form.values.badges[badgeIndex].max_points}
                          onChange={form.handleChange}
                          onBlur={form.handleBlur}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50"
                          min="1"
                          disabled
                        />
                        {renderFieldError(`badges.${badgeIndex}.max_points`)}
                      </div>
                    </div>
                  </div>

                  <div>
                    <TextArea
                      id={`badges.${badgeIndex}.description`}
                      placeholder="Entry level badge, etc."
                      label="Description"
                      {...form}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <TextInput
                        label="Minimum Cash Equivalent (GHS ₵)"
                        id={`badges[${badgeIndex}].minimum_cash_equivalent`}
                        {...form}
                        preText="₵"
                        type="number"
                        min={0}
                      />
                    </div>
                    <div>
                      <TextInput
                        label="Cash Equivalent (GHS ₵)"
                        id={`badges[${badgeIndex}].cash_equivalent`}
                        {...form}
                        preText="₵"
                        type="number"
                        min={0}
                      />
                    </div>
                  </div>

                  {/* Gift Basket Section */}
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-md font-medium text-gray-900">
                        Gift Basket (Max 5)
                      </h3>
                      <OfficeButton
                        disabled={
                          badge?.gift_basket?.length &&
                          badge?.gift_basket?.length >= 5
                            ? true
                            : false
                        }
                        onClick={() => addGift(badgeIndex)}
                      >
                        <PlusIcon className="h-4 w-4 mr-1" /> Add Gift
                      </OfficeButton>
                    </div>

                    <FieldArray
                      name={`badges[${badgeIndex}].gift_basket`}
                      render={() => (
                        <div className="space-y-4">
                          {badge?.gift_basket?.length === 0 && (
                            <div className="text-sm text-gray-500 italic">
                              No gifts added yet
                            </div>
                          )}

                          {badge?.gift_basket?.map(
                            (gift: any, giftIndex: number) => (
                              <div
                                key={giftIndex}
                                className="p-3 border border-gray-200 rounded-md space-y-3"
                              >
                                <div className="flex items-center justify-between">
                                  <h4 className="text-sm font-medium">
                                    Gift #{giftIndex + 1}
                                  </h4>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      removeGift(badgeIndex, giftIndex)
                                    }
                                    className="inline-flex items-center p-1 text-red-500 hover:text-red-700"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>

                                <div>
                                  <TextInput
                                    id={`badges[${badgeIndex}].gift_basket[${giftIndex}].gift_name`}
                                    {...form}
                                  />
                                </div>

                                <div>
                                  <RegularSelectInput
                                    label={"Gift Type"}
                                    options={[
                                      ...GIFT_TYPES.map((el) => ({
                                        value: el,
                                        label: el,
                                      })),
                                    ]}
                                    id={`badges[${badgeIndex}].gift_basket[${giftIndex}].gift_type`}
                                    {...form}
                                  />
                                </div>

                                <div>
                                  <TextInput
                                    label="Gift Image URL"
                                    placeholder="https://example.com/image.jpg"
                                    id={`badges[${badgeIndex}].gift_basket[${giftIndex}].gift_image`}
                                    {...form}
                                  />
                                </div>

                                <div>
                                  <TextArea
                                    label="Gift Description"
                                    id={`badges[${badgeIndex}].gift_basket[${giftIndex}].gift_description`}
                                    {...form}
                                  />
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>
            );
          }
        )}
      </div>
    </FormikProvider>
  );
};

export default CreateTargetBadgeForm;
